/**
 * useWizardState Hook
 * 
 * Encapsulates all Board Creation Wizard state management and business logic.
 * Makes the component purely presentational by handling:
 * - State initialization from saved progress
 * - All state transitions via reducer
 * - Progress persistence to localStorage
 * - Tracking integration
 * - Event handlers
 */

import { useEffect, useCallback, useRef } from "react";

import { useNavigate } from "react-router-dom";

import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext"; // Added import
import { useBoardCreationTracking } from "@/hooks/useBoardCreationTracking";
import { useEdgeFunction } from "@/hooks/useEdgeFunction";
import { streamAuthenticatedEdgeFunction } from "@/lib/edgeFunctionHelpers";
import { ROUTES } from "@/lib/routes";
import { trackUserAction, trackButtonClick, trackDataChange } from "@/lib/sentryBreadcrumbs";
import { useReducerWithDevtools } from "@/lib/useReducerWithDevtools";
import {
  saveWizardProgress,
  loadWizardProgress,
  clearWizardProgress,
} from "@/lib/wizardProgress";
import { wizardReducer, initialWizardState, wizardActions } from "@/reducers/wizardReducer";
import { WizardPath, SuggestedPersona } from "@/types/state";

// Helper to process stream response with buffering
async function processStream(
  stream: ReadableStream<Uint8Array>,
  dispatch: any,
  onEnd: (data: any) => void
) {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      
      const lines = buffer.split('\n\n');
      // Keep the last part in buffer as it might be incomplete
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith('data: ')) {
          try {
            const json = JSON.parse(trimmedLine.substring(6));
            if (json.event === 'update') {
              dispatch(wizardActions.updateGenerationStatus(json.data));
            } else if (json.event === 'end') {
              onEnd(json.data);
            } else if (json.event === 'error') {
              toast.error(json.data.message || "Generation failed");
            }
          } catch (e) {
            console.error("Error parsing stream JSON:", e, "Line:", trimmedLine);
          }
        }
      }
    }
  } catch (e) {
    console.error("Streaming error:", e);
    toast.error("An error occurred during generation.");
    throw e;
  }
}

export function useWizardState() {
  const navigate = useNavigate();
  const { user } = useAuth(); // Get user
  const { execute: invoke, isLoading: isSubmitting } = useEdgeFunction({ auth: true });
  const { sessionId, startTracking, trackStep, updateFlowType, markCompleted } = useBoardCreationTracking();

  // Initialize state with default values
  const [state, dispatch] = useReducerWithDevtools(
    wizardReducer,
    initialWizardState,
    'BoardCreationWizard'
  );

  // Load saved progress on mount
  useEffect(() => {
    const loadProgress = async () => {
      if (!user?.id) return;
      
      const savedProgress = await loadWizardProgress(user.id);
      
      if (savedProgress.timestamp > 0 && (savedProgress.currentStep !== 'path-selection' || savedProgress.selectedPath !== null)) {
        dispatch(wizardActions.initialize({
          currentStep: savedProgress.currentStep || 'path-selection',
          selectedPath: savedProgress.selectedPath || null,
          problem: savedProgress.problem || '',
          context: savedProgress.context || '',
          brainDump: savedProgress.brainDump || '',
          suggestedPersonas: savedProgress.suggestedPersonas || [],
          showResumePrompt: true,
          lastSaved: savedProgress.timestamp,
          remixBoard: savedProgress.remixBoard || null,
        }));
      }
    };

    loadProgress();
  }, [user?.id]);

  // Sync state to server on changes
  const prevStateRef = useRef(state);
  useEffect(() => {
    if (!user?.id) return;
    
    // Don't save if it's the default initial state
    if (state.currentStep === 'path-selection' && !state.selectedPath) return;

    const prevState = prevStateRef.current;
    let debounce = false;

    // Debounce text input changes
    if (
      state.problem !== prevState.problem ||
      state.context !== prevState.context ||
      state.brainDump !== prevState.brainDump
    ) {
      debounce = true;
    }

    saveWizardProgress(
      user.id,
      {
        currentStep: state.currentStep,
        selectedPath: state.selectedPath,
        problem: state.problem,
        context: state.context,
        brainDump: state.brainDump,
        suggestedPersonas: state.suggestedPersonas,
        remixBoard: state.remixBoard,
      },
      debounce,
      () => dispatch(wizardActions.markSaved(Date.now()))
    );

    prevStateRef.current = state;
  }, [state, user?.id]);

  // Start tracking on mount
  useEffect(() => {
    if (!sessionId) {
      startTracking();
    }
  }, [sessionId, startTracking]);

  // Removed manual updateProgress function

  /**
   * Handle path selection (prebuilt, custom, or braindump)
   */
  const handlePathSelection = useCallback(
    (path: WizardPath) => {
      dispatch(wizardActions.selectPath(path));
      // updateProgress removed - handled by useEffect

      // Track analytics
      trackUserAction(`path_selected_${path}`, "board_creation", { path });
      updateFlowType(path);

      // Track step after state update
      setTimeout(() => {
        if (sessionId) {
          const newStep =
            path === 'prebuilt'
              ? 'prebuilt-selection'
              : path === 'custom'
              ? 'custom-form'
              : 'braindump-form';
          trackStep(sessionId, newStep);
        }
      }, 0);
    },
    [dispatch, updateFlowType, sessionId, trackStep]
  );

  /**
   * Handle remixing a prebuilt board
   */
  const handleRemix = useCallback(
    (board: any) => {
      dispatch(wizardActions.startRemix(board));
      
      // Track analytics
      trackUserAction("board_remix_started", "board_creation", { 
        boardId: board.id, 
        boardName: board.name 
      });
      
      if (sessionId) {
        trackStep(sessionId, 'custom-form');
        updateFlowType('custom'); // Remix flows through custom path
      }
    },
    [dispatch, sessionId, trackStep, updateFlowType]
  );

  /**
   * Handle back navigation
   */
  const handleBack = useCallback(() => {
    trackButtonClick("board_creation_back", { currentStep: state.currentStep });

    if (state.currentStep === "path-selection") {
      // Already at first step - exit wizard and return to dashboard
      if (user?.id) clearWizardProgress(user.id);
      navigate(ROUTES.DASHBOARD);
    } else {
      // Go back to previous step
      dispatch(wizardActions.goBack());
      // updateProgress removed - handled by useEffect

      if (sessionId) {
        // Track the step we're going back to
        setTimeout(() => {
          trackStep(sessionId, state.currentStep);
        }, 0);
      }
    }
  }, [state.currentStep, dispatch, navigate, sessionId, trackStep, user?.id]);

  /**
   * Handle error reset
   */
  const handleErrorReset = useCallback(() => {
    dispatch(wizardActions.resetWizard());
    if (user?.id) clearWizardProgress(user.id);
  }, [dispatch, user?.id]);

  /**
   * Handle resume prompt - user wants to continue
   */
  const handleResumeProgress = useCallback(() => {
    dispatch(wizardActions.hideResumePrompt());
  }, [dispatch]);

  /**
   * Handle start fresh - user wants to discard progress
   */
  const handleStartFresh = useCallback(() => {
    dispatch(wizardActions.resetWizard());
    if (user?.id) clearWizardProgress(user.id);
  }, [dispatch, user?.id]);

  /**
   * Handle board creation completion
   */
  const handleBoardCreated = useCallback(() => {
    trackDataChange("board", "create");
    trackUserAction("board_creation_completed", "board_creation", {
      boardType: state.selectedPath,
    });
    dispatch(wizardActions.completeWizard());
    if (user?.id) clearWizardProgress(user.id);
  }, [state.selectedPath, dispatch, user?.id]);

  /**
   * Update problem field
   */
  const handleProblemChange = useCallback(
    (value: string) => {
      dispatch(wizardActions.updateProblem(value));
      // updateProgress removed - handled by useEffect
    },
    [dispatch]
  );

  /**
   * Update context field
   */
  const handleContextChange = useCallback(
    (value: string) => {
      dispatch(wizardActions.updateContext(value));
      // updateProgress removed - handled by useEffect
    },
    [dispatch]
  );

  /**
   * Update brain dump field
   */
  const handleBrainDumpChange = useCallback(
    (value: string) => {
      dispatch(wizardActions.updateBrainDump(value));
      // updateProgress removed - handled by useEffect
    },
    [dispatch]
  );

  /**
   * Handle custom form submission
   */
  const handleCustomFormSubmit = useCallback(
    async (refinementMode: 'manual' | 'automated') => {
      trackButtonClick(`custom_form_submit_${refinementMode}`);
      dispatch(wizardActions.startGeneration());

      const isHumanInLoop = refinementMode === 'manual';

      // Construct context with remix data if available
      let finalContext = state.context;
      if (state.remixBoard) {
        const remixContext = `[REMIX INSTRUCTION]\nI am remixing the "${state.remixBoard.name}" template.\nTemplate Description: ${state.remixBoard.description}\nTemplate Personas: ${state.remixBoard.personas.map((p: any) => p.role_name).join(', ')}.\n\nPlease adapt this template to solve my specific problem/context below. Keep the core strengths of the template but tailor it to my needs.`;
        finalContext = finalContext ? `${remixContext}\n\n[MY CONTEXT]\n${finalContext}` : remixContext;
      }

      const stream = await streamAuthenticatedEdgeFunction("generate-board", {
        body: {
          generationMode: 'problem',
          problem: state.problem,
          context: finalContext,
          refinementMode: isHumanInLoop ? 'human' : 'ai',
          stream: true,
        }
      });

      if (!stream) {
        dispatch(wizardActions.endGeneration());
        toast.error("Failed to start generation process.");
        return;
      }

      try {
        await processStream(stream, dispatch, (data) => {
          if (isHumanInLoop) {
            // First interruption: board details are ready for review
            dispatch(wizardActions.setPartialBoard({
              boardDetails: data.boardDetails,
              threadId: data.thread_id,
            }));
          } else {
            // Automated flow finished
            dispatch(wizardActions.setGeneratedBoard({
              boardDetails: data.boardDetails,
              personas: data.personas
            }));
          }
          // updateProgress removed - handled by useEffect
        });
      } catch (e) {
        // Error handled in processStream
      } finally {
        dispatch(wizardActions.endGeneration());
      }
    },
    [state.problem, state.context, state.remixBoard, dispatch]
  );

  /**
   * Handle submission of feedback for board details
   */
  const handleBoardDetailsFeedbackSubmit = useCallback(
    async (feedback: string) => {
      trackButtonClick('board_details_feedback_submit');
      dispatch(wizardActions.startGeneration());

      const stream = await streamAuthenticatedEdgeFunction("generate-board", {
        body: {
          thread_id: state.threadId,
          feedback: feedback,
          refinementMode: 'human',
          stream: true,
        }
      });

      if (!stream) {
        dispatch(wizardActions.endGeneration());
        toast.error("Failed to continue generation process.");
        return;
      }

      try {
        await processStream(stream, dispatch, (data) => {
          // Second interruption: personas are ready for review
          dispatch(wizardActions.setPartialBoard({
            boardDetails: data.boardDetails,
            personas: data.personas,
            threadId: data.thread_id,
          }));
          // updateProgress removed - handled by useEffect
        });
      } catch (e) {
        // Error handled in processStream
      } finally {
        dispatch(wizardActions.endGeneration());
      }
    },
    [state.threadId, dispatch]
  );

  /**
   * Handle submission of feedback for personas
   */
  const handlePersonaFeedbackSubmit = useCallback(
    async (feedback: string) => {
      trackButtonClick('persona_feedback_submit');
      dispatch(wizardActions.startGeneration());

      const stream = await streamAuthenticatedEdgeFunction("generate-board", {
        body: {
          thread_id: state.threadId,
          personaFeedback: feedback,
          refinementMode: 'human',
          stream: true,
        }
      });

      if (!stream) {
        dispatch(wizardActions.endGeneration());
        toast.error("Failed to finalize generation process.");
        return;
      }

      try {
        await processStream(stream, dispatch, (data) => {
          // Final result
          dispatch(wizardActions.setGeneratedBoard({
            boardDetails: data.boardDetails,
            personas: data.personas,
          }));
          // updateProgress removed - handled by useEffect
        });
      } catch (e) {
        // Error handled in processStream
      } finally {
        dispatch(wizardActions.endGeneration());
      }
    },
    [state.threadId, dispatch]
  );

  /**
   * Handle brain dump form submission
   */
  const handleBrainDumpSubmit = useCallback(
    async (refinementMode: 'manual' | 'automated') => {
      trackButtonClick(`braindump_form_submit_${refinementMode}`);

      if (refinementMode === 'manual') {
        const { data, error } = await invoke("suggest-experts", {
          body: {
            brainDump: state.brainDump,
            type: "braindump"
          },
          showErrorToast: true
        });

        if (error) return;

        const personas: SuggestedPersona[] = data.experts.map((expert: any, idx: number) => ({
          id: `temp-${idx}`,
          role_name: expert.role_name,
          justification: expert.justification
        }));

        handlePersonasGenerated(personas);
      } else {
        dispatch(wizardActions.startGeneration());
        const stream = await streamAuthenticatedEdgeFunction("generate-board", {
          body: {
            generationMode: 'braindump',
            brainDump: state.brainDump,
            refinementMode: 'ai'
          }
        });

        if (!stream) {
          dispatch(wizardActions.endGeneration());
          toast.error("Failed to start generation process.");
          return;
        }

        try {
          await processStream(stream, dispatch, (data) => {
            dispatch(wizardActions.setGeneratedBoard({
              boardDetails: data.boardDetails,
              personas: data.personas
            }));
            // updateProgress removed - handled by useEffect
          });
        } catch (e) {
          // Error handled in processStream
        } finally {
          dispatch(wizardActions.endGeneration());
        }
      }
    },
    [state.brainDump, invoke, dispatch, handlePersonasGenerated]
  );

  /**
   * Handle persona suggestions received from AI
   */
  const handlePersonasGenerated = useCallback(
    (personas: SuggestedPersona[]) => {
      dispatch(wizardActions.setSuggestedPersonas(personas));
      // updateProgress removed - handled by useEffect

      if (sessionId) {
        trackStep(sessionId, "persona-suggestions");
      }
    },
    [dispatch, sessionId, trackStep]
  );

  /**
   * Update personas (user editing suggestions)
   */
  const handleUpdatePersonas = useCallback(
    (personas: SuggestedPersona[]) => {
      dispatch(wizardActions.setSuggestedPersonas(personas));
      // updateProgress removed - handled by useEffect
    },
    [dispatch]
  );

  /**
   * Handle successful board creation
   */
  const handleBoardSuccess = useCallback(
    (boardId: string) => {
      if (sessionId) {
        markCompleted(boardId);
      }
      handleBoardCreated();
    },
    [sessionId, markCompleted, handleBoardCreated]
  );

  return {
    // State
    state,

    // Event handlers
    handlePathSelection,
    handleRemix,
    handleBack,
    handleErrorReset,
    handleResumeProgress,
    handleStartFresh,
    handleBoardCreated,
    handleProblemChange,
    handleContextChange,
    handleBrainDumpChange,
    handleCustomFormSubmit,
    handleBrainDumpSubmit,
    handleBoardDetailsFeedbackSubmit,
    handlePersonaFeedbackSubmit,
    handlePersonasGenerated,
    handleUpdatePersonas,
    handleBoardSuccess,

    // Tracking
    sessionId,
  };
}
